import { useFormikContext } from "formik";
import { differenceInDays, addMonths, addYears } from "date-fns";

/**
 * Custom hook that encapsulates all loan calculation logic.
 * Must be used inside a Formik descendant component.
 */
const useLoanCalculations = () => {
    const { values, setFieldValue, setValues } = useFormikContext();

    // -------------------------------
    // FIXED INTEREST (FLAT RATE)
    // -------------------------------
    const calculateFixedInterest = (flag) => {
        if (
            (values.interestType === "fixed" || flag) &&
            values.loanAmount &&
            values.interestPerYear
        ) {
            let tenureMonths = 0;

            if (values.durationYears !== "" && values.durationMonths !== "") {
                const years = parseFloat(values.durationYears) || 0;
                const months = parseFloat(values.durationMonths) || 0;
                tenureMonths = years * 12 + months;
            } else {
                setFieldValue("totalInterest", "", false);
                setFieldValue("totalAmountPayable", "", false);
                return;
            }

            if (tenureMonths <= 0) {
                setFieldValue("totalInterest", "", false);
                setFieldValue("totalAmountPayable", "", false);
                return;
            }

            const principal = parseFloat(values.loanAmount) || 0;
            const annualRate = parseFloat(values.interestPerYear) || 0;

            // Step 1: Calculate broken period interest and add to principal
            let newPrincipal = principal;
            if (values.firstEMIDate && values.loanStartDate) {
                const brokenDays = differenceInDays(values.firstEMIDate, values.loanStartDate);
                if (brokenDays > 0) {
                    const brokenInterest = principal * (annualRate / 100) * (brokenDays / 365);
                    newPrincipal = principal + brokenInterest;
                }
            }

            // Step 2: FLAT INTEREST on New Principal for tenure
            const interest = newPrincipal * (annualRate / 100) * (tenureMonths / 12);
            // Step 3: Total Payable = New Principal + Interest
            const totalPayable = newPrincipal + interest;

            setFieldValue("totalInterest", interest.toFixed(2), false);
            setFieldValue("totalAmountPayable", totalPayable.toFixed(2), false);
            if (values.monthlyInstallment) {
                const monthlyInstallmentAmount = totalPayable / tenureMonths;
                setFieldValue("monthlyInstallmentAmount", monthlyInstallmentAmount.toFixed(2), false);
                setFieldValue("totalInstallments", tenureMonths.toString(), false);
            }
        } else {
            setFieldValue("totalInterest", "", false);
            setFieldValue("totalAmountPayable", "", false);
        }
    };

    // -------------------------------
    // REDUCING BALANCE EMI (BANK METHOD)
    // -------------------------------
    const calculateMonthlyInstallment = (flag) => {
        if (
            (values.monthlyInstallment || flag) &&
            values.loanAmount &&
            values.interestPerYear
        ) {
            let tenureMonths = 0;

            if (values.durationYears !== "" && values.durationMonths !== "") {
                const years = parseFloat(values.durationYears) || 0;
                const months = parseFloat(values.durationMonths) || 0;
                tenureMonths = years * 12 + months;
            } else {
                setFieldValue("monthlyInstallmentAmount", "", false);
                return;
            }

            if (tenureMonths <= 0) {
                setFieldValue("monthlyInstallmentAmount", "", false);
                return;
            }

            const totalAmountPayable = parseFloat(values.totalAmountPayable) || 0;

            if (totalAmountPayable <= 0) {
                setFieldValue("monthlyInstallmentAmount", "", false);
                return;
            }

            const monthlyInstallmentAmount = totalAmountPayable / tenureMonths;
            setFieldValue("monthlyInstallmentAmount", monthlyInstallmentAmount.toFixed(2), false);

            if (values.monthlyInstallment && tenureMonths > 0) {
                setFieldValue("totalInstallments", tenureMonths.toString(), false);
            }
        } else {
            setFieldValue("monthlyInstallmentAmount", "", false);
        }
    };

    // -------------------------------
    // CALCULATE LOAN END DATE BASED ON REPAYMENT TYPE
    // -------------------------------
    const calculateLoanEndDate = () => {
        if (!values.loanStartDate || values.durationYears === "" || values.durationMonths === "") {
            return null;
        }

        if (values.monthlyInstallment && values.firstEMIDate) {
            const years = parseFloat(values.durationYears) || 0;
            const months = parseFloat(values.durationMonths) || 0;
            const tenureMonths = years * 12 + months;
            const loanEndDate = addMonths(values.firstEMIDate, tenureMonths - 1);
            return loanEndDate;
        } else {
            let endDate = new Date(values.loanStartDate);
            const years = parseFloat(values.durationYears) || 0;
            const months = parseFloat(values.durationMonths) || 0;
            endDate = addYears(endDate, years);
            endDate = addMonths(endDate, months);
            return endDate;
        }
    };

    // -------------------------------
    // REDUCTIBLE INTEREST (REDUCING BALANCE TOTAL INTEREST)
    // -------------------------------
    const calculateReductibleInterest = (flag) => {
        if (
            (values.interestType === "reducing" || flag) &&
            values.loanAmount &&
            values.interestPerYear
        ) {
            let tenureMonths = 0;

            if (values.durationYears !== "" && values.durationMonths !== "") {
                const years = parseFloat(values.durationYears) || 0;
                const months = parseFloat(values.durationMonths) || 0;
                tenureMonths = years * 12 + months;
            } else {
                setFieldValue("totalInterest", "", false);
                setFieldValue("totalAmountPayable", "", false);
                setFieldValue("monthlyInstallmentAmount", "", false);
                return;
            }

            if (tenureMonths <= 0) {
                setFieldValue("totalInterest", "", false);
                setFieldValue("totalAmountPayable", "", false);
                setFieldValue("monthlyInstallmentAmount", "", false);
                return;
            }

            const principal = parseFloat(values.loanAmount) || 0;
            const annualRate = parseFloat(values.interestPerYear) || 0;

            // Step 1: Calculate broken period interest and add to principal
            let newPrincipal = principal;
            if (values.firstEMIDate && values.loanStartDate) {
                const brokenDays = differenceInDays(values.firstEMIDate, values.loanStartDate);
                if (brokenDays > 0) {
                    const brokenInterest = principal * (annualRate / 100) * (brokenDays / 365);
                    newPrincipal = principal + brokenInterest;
                }
            }

            // Step 2: Calculate EMI using reducing formula on New Principal
            const monthlyRate = annualRate / 12 / 100;
            const onePlus = 1 + monthlyRate;
            const power = Math.pow(onePlus, tenureMonths);
            const emi = (newPrincipal * monthlyRate * power) / (power - 1);

            // Step 3: Total Paid & Total Interest (vs original principal)
            const totalPaid = emi * tenureMonths;
            const totalInterest = totalPaid - principal;

            setFieldValue("monthlyInstallmentAmount", emi.toFixed(2), false);
            setFieldValue("totalInterest", totalInterest.toFixed(2), false);
            setFieldValue("totalAmountPayable", totalPaid.toFixed(2), false);
            setFieldValue("totalInstallments", tenureMonths.toString(), false);
        } else {
            setFieldValue("totalInterest", "", false);
            setFieldValue("totalAmountPayable", "", false);
            setFieldValue("monthlyInstallmentAmount", "", false);
        }
    };

    // Handle first EMI date change with full recalculation
    const handleFirstEMIDateChange = (date) => {
        const years = parseFloat(values.durationYears) || 0;
        const months = parseFloat(values.durationMonths) || 0;
        const tenureMonths = years * 12 + months;

        // Calculate new loan end date
        let newLoanEndDate = values.loanEndDate;
        if (date && tenureMonths > 0) {
            newLoanEndDate = addMonths(date, tenureMonths - 1);
        } else if (values.loanStartDate && values.durationYears !== "" && values.durationMonths !== "") {
            let endDate = new Date(values.loanStartDate);
            endDate = addYears(endDate, years);
            endDate = addMonths(endDate, months);
            newLoanEndDate = endDate;
        }

        const principal = parseFloat(values.loanAmount) || 0;
        const annualRate = parseFloat(values.interestPerYear) || 0;
        let newTotalInterest = values.totalInterest;
        let newTotalAmountPayable = values.totalAmountPayable;
        let newMonthlyInstallmentAmount = values.monthlyInstallmentAmount;
        let newTotalInstallments = values.totalInstallments;

        if (principal > 0 && annualRate > 0 && tenureMonths > 0) {
            // Step 1: Broken Period Interest
            let newPrincipal = principal;
            if (date && values.loanStartDate) {
                const brokenDays = differenceInDays(date, values.loanStartDate);
                if (brokenDays > 0) {
                    const brokenInterest = principal * (annualRate / 100) * (brokenDays / 365);
                    newPrincipal = principal + brokenInterest;
                }
            }

            if (values.interestType === "fixed") {
                const interest = newPrincipal * (annualRate / 100) * (tenureMonths / 12);
                const totalPayable = newPrincipal + interest;
                newTotalInterest = interest.toFixed(2);
                newTotalAmountPayable = totalPayable.toFixed(2);
                if (values.monthlyInstallment) {
                    newMonthlyInstallmentAmount = (totalPayable / tenureMonths).toFixed(2);
                    newTotalInstallments = tenureMonths.toString();
                }
            } else if (values.interestType === "reducing") {
                const monthlyRate = annualRate / 12 / 100;
                const onePlus = 1 + monthlyRate;
                const power = Math.pow(onePlus, tenureMonths);
                const emi = (newPrincipal * monthlyRate * power) / (power - 1);
                const totalPaid = emi * tenureMonths;
                const totalInterest = totalPaid - principal;
                newTotalInterest = totalInterest.toFixed(2);
                newTotalAmountPayable = totalPaid.toFixed(2);
                newMonthlyInstallmentAmount = emi.toFixed(2);
                newTotalInstallments = tenureMonths.toString();
            }
        }

        // Set all values at once to avoid stale closure issues
        setValues({
            ...values,
            firstEMIDate: date,
            loanEndDate: newLoanEndDate,
            totalInterest: newTotalInterest,
            totalAmountPayable: newTotalAmountPayable,
            monthlyInstallmentAmount: newMonthlyInstallmentAmount,
            totalInstallments: newTotalInstallments,
        });
    };

    return {
        calculateFixedInterest,
        calculateMonthlyInstallment,
        calculateLoanEndDate,
        calculateReductibleInterest,
        handleFirstEMIDateChange,
    };
};

export default useLoanCalculations;
